/**
 * APM Click Tracking Enhancement
 * 
 * Automatically extracts meaningful names for user interactions from buttons/elements.
 * 
 * Priority order for name extraction:
 * 1. data-apm-name attribute (explicit naming)
 * 2. aria-label attribute (recommended - improves accessibility)
 * 3. title attribute
 * 4. Button text content
 * 5. Class names (fallback)
 * 
 * Examples:
 * <button aria-label="Upload Patient Radiographs">Upload</button>
 * <button data-apm-name="Save Patient">Save</button>
 * <button title="Run AI Analysis">Analyze</button>
 * 
 * APM will use these names instead of generic "Click - button"
 */

/**
 * Get a meaningful name from an element
 */
function getElementName(element: HTMLElement): string | null {
  // 1. Check for explicit data-apm-name attribute (highest priority)
  const explicitName = element.getAttribute('data-apm-name');
  if (explicitName?.trim()) return explicitName.trim();

  // 2. Check for aria-label (good for accessibility)
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel?.trim()) return ariaLabel.trim();

  return null;
}

// Store the last clicked element name temporarily (module-level)
let lastClickedName: string | null = null;

export const initializeApmClickTracking = () => {
  // Listen for all clicks (before APM processes them)
  document.addEventListener('click', (event) => {
    try {
      const target = event.target as HTMLElement;
      
      // Find clickable element (handles SVG children automatically via closest())
      const clickableElement = target.closest(
        'button, a, [role="button"], [role="link"], input[type="submit"], input[type="button"], [data-apm-name]'
      ) as HTMLElement;
      
      if (clickableElement) {
        const transactionName = getElementName(clickableElement);
        if (transactionName) {
          lastClickedName = transactionName;
        }
      }
    } catch (error) {
      // Silently fail - don't break user clicks if APM tracking fails
      console.error('[APM] Click tracking error:', error);
    }
  }, { capture: true });
};

/**
 * Get the last clicked element name (used by filter)
 * Clears the stored name after reading to prevent reuse
 */
export const getLastClickedName = () => {
  const name = lastClickedName;
  lastClickedName = null; // Clear immediately after reading
  return name;
};
